﻿/**
 * MVVM wiring for {@link DatePicker}, in the role MvvmInput plays for Input: the three date/time
 * controls differ only in the type they carry and how it reads as a string, so everything else —
 * context, id, validation, subscription, write-back — is written once here.
 */

import {DateOnly} from "@2d-front-monorepo/core";
import {JSX, SyntheticEvent, useCallback} from "react";
import {useControlId} from "../helpers/useControlId";
import DatePicker, {DatePickerMessages, DatePickerMode} from "../HtmlControls/DatePicker";
import {MvvmPrefixedControlProps} from "./MvvmControlProps";
import {useMvvmFieldSubscription} from "./MvvmDataProvider";
import {unwrapMvvmContext} from "./useMvvmTracking";
import {useRegisterValidation} from "./useRegisterValidation";

export interface MvvmPickerProps<T extends object, TValue> {
	control: MvvmPrefixedControlProps<T, TValue>;
	mode: DatePickerMode;
	valueToString: (value: TValue | undefined) => string;
	valueFromString: (value: string | undefined) => TValue | undefined;
	min?: string;
	max?: string;
	step?: number | "any";
	autoAccept?: boolean;
	messages?: DatePickerMessages;
	isSelectable?: (date: DateOnly) => boolean;
	dayClassName?: (date: DateOnly) => string | undefined;
}

export default function MvvmPicker<T extends object = never, TValue = never>(props: MvvmPickerProps<T, TValue>): JSX.Element {
	const {control, mode, valueToString, valueFromString, min, max, step, autoAccept, messages, isSelectable, dayClassName} = props;

	const {
		context: contextProp,
		property,
		id,
		label,
		hint,
		column,
		controlPrefix,
		controlSuffix,
		hidden,
		disabled,
		inlineError,
		validation,
		autoFocus,
		onChange,
		onBlur
	} = control;

	// Controls read the raw context; see unwrapMvvmContext.
	const context = unwrapMvvmContext(contextProp);
	const controlId = useControlId(id);
	const value = valueToString(context.getValue<TValue>(property));

	const onChangeInternal = useCallback(function (e: SyntheticEvent<HTMLInputElement>): void {
		const value = valueFromString(e.currentTarget.value);
		onChange?.(value);
		context.setValue(property, value);
	}, [context, property, onChange, valueFromString]);

	useRegisterValidation(context, property, controlId, hidden, validation);
	useMvvmFieldSubscription(context, property);

	return <DatePicker label={label}
	                   name={property}
	                   hint={hint}
	                   column={column}
	                   controlPrefix={controlPrefix}
	                   controlSuffix={controlSuffix}
	                   hidden={hidden}
	                   disabled={disabled}
	                   autoFocus={autoFocus}
	                   mode={mode}
	                   min={min}
	                   max={max}
	                   step={step}
	                   autoAccept={autoAccept}
	                   messages={messages}
	                   id={id}
	                   required={validation?.required != null}
	                   error={context.getError(property)?.error}
	                   inlineError={inlineError}
	                   isSelectable={isSelectable}
	                   dayClassName={dayClassName}
	                   value={value}
	                   onChange={onChangeInternal}
	                   onBlur={onBlur}/>;
};
